import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import "./MetricCarousel.css";

const CAROUSEL_THRESHOLD = 5;
const ADD_ITEM_ID = "__add_metric__";

export default function MetricCarousel({
  cards = [],
  renderCard,
  onAddClick, // gardé pour compatibilité
  onAddDirect,
  isEditMode,
  customCards,
  onRemoveCustom,
  showAddButton = true,
  className = "",
}) {
  const allMetricIds = useMemo(() => [...cards], [cards]);

  // Keep the add-card slot for editable views; read-only views omit it.
  const carouselItems = useMemo(() => {
    return showAddButton ? [...allMetricIds, ADD_ITEM_ID] : allMetricIds;
  }, [allMetricIds, showAddButton]);

  const isCarousel = allMetricIds.length >= CAROUSEL_THRESHOLD;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState("next");
  const trackRef = useRef(null);

  const [inlineCard, setInlineCard] = useState(null);
  const labelRef = useRef(null);

  // ✅ Anti double-submit : pointerDown + click peuvent arriver ensemble
  const submitLockRef = useRef(false);

  const [perPage, setPerPage] = useState(4);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth >= 1200) setPerPage(4);
      else if (window.innerWidth >= 768) setPerPage(3);
      else if (window.innerWidth >= 480) setPerPage(2);
      else setPerPage(1);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const totalPages = Math.max(1, Math.ceil(carouselItems.length / perPage));
  const clampedIndex = Math.min(currentIndex, Math.max(0, totalPages - 1));
  const isLastPage = clampedIndex >= totalPages - 1;

  const visibleItems = carouselItems.slice(
    clampedIndex * perPage,
    clampedIndex * perPage + perPage
  );

  const navigate = useCallback(
    (dir) => {
      if (isAnimating) return;

      setDirection(dir);
      setIsAnimating(true);

      setTimeout(() => {
        setCurrentIndex((prev) => {
          if (dir === "next") return Math.min(prev + 1, totalPages - 1);
          return Math.max(prev - 1, 0);
        });

        setIsAnimating(false);
      }, 280);
    },
    [isAnimating, totalPages]
  );

  useEffect(() => {
    if (!isCarousel) setCurrentIndex(0);
  }, [isCarousel]);

  const handleAddInline = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();


      setInlineCard({ label: "", value: "", unit: "" });

      const lastPage = Math.ceil(carouselItems.length / perPage) - 1;
      setCurrentIndex(Math.max(0, lastPage));

      setTimeout(() => {
        labelRef.current?.focus();
      }, 80);
    },
    [carouselItems.length, perPage]
  );

  const handleCancel = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();


    setInlineCard(null);
  }, []);

  const submitInlineCard = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();

      if (submitLockRef.current) return;
      submitLockRef.current = true;

      setTimeout(() => {
        submitLockRef.current = false;
      }, 400);


      if (!inlineCard) {
        console.error("❌ inlineCard est null");
        return;
      }

      const label = String(inlineCard.label || "").trim();
      const value = String(inlineCard.value || "").trim();
      const unit = String(inlineCard.unit || "").trim();

      if (!label && !value) {
        console.warn("⚠️ Carte vide, fermeture sans sauvegarde");
        setInlineCard(null);
        return;
      }

      const payload = {
        label: label || "Métrique",
        value: value || "0",
        unit,
        tone: "low",
        emoji: "💊",
      };


      if (typeof onAddDirect === "function") {
        onAddDirect(payload);
      } else if (typeof onAddClick === "function") {
        onAddClick(payload);
      } else {
        console.error("❌ Aucun callback d'ajout trouvé : onAddDirect / onAddClick");
      }

      setInlineCard(null);
    },
    [inlineCard, onAddDirect, onAddClick]
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        handleCancel(e);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleCancel]);

  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        submitInlineCard(e);
      }

      if (e.key === "Escape") {
        handleCancel(e);
      }
    },
    [submitInlineCard, handleCancel]
  );

  const InlineCard = inlineCard && (
    <div
      className="mc-inline-card"
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mc-inline-card__icon">💊</div>

      <div className="mc-inline-card__fields">
        <input
          ref={labelRef}
          className="mc-inline-input mc-inline-input--label"
          type="text"
          placeholder="Nom"
          value={inlineCard.label}
          onChange={(e) =>
            setInlineCard((p) => ({
              ...p,
              label: e.target.value,
            }))
          }
          onKeyDown={handleInputKeyDown}
          maxLength={32}
        />

        <div className="mc-inline-row">
          <input
            className="mc-inline-input mc-inline-input--value"
            type="text"
            placeholder="Valeur"
            value={inlineCard.value}
            onChange={(e) =>
              setInlineCard((p) => ({
                ...p,
                value: e.target.value,
              }))
            }
            onKeyDown={handleInputKeyDown}
            maxLength={20}
          />

          <input
            className="mc-inline-input mc-inline-input--unit"
            type="text"
            placeholder="Unité"
            value={inlineCard.unit}
            onChange={(e) =>
              setInlineCard((p) => ({
                ...p,
                unit: e.target.value,
              }))
            }
            onKeyDown={handleInputKeyDown}
            maxLength={10}
          />
        </div>
      </div>

      <div className="mc-inline-card__actions">
        <button
          type="button"
          className="mc-inline-btn mc-inline-btn--confirm"
          title="Confirmer"
          onPointerDown={submitInlineCard}
          onClick={submitInlineCard}
        >
          ✓
        </button>

        <button
          type="button"
          className="mc-inline-btn mc-inline-btn--cancel"
          title="Annuler"
          onPointerDown={handleCancel}
          onClick={handleCancel}
        >
          ×
        </button>
      </div>
    </div>
  );

  const renderAddOrInline = () => {
    if (inlineCard) return InlineCard;

    return (
      <button
        type="button"
        className="mc-add-card-btn"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onClick={handleAddInline}
      >
        <span className="mc-add-card-btn__circle">+</span>
      </button>
    );
  };

  // ── GRID mode (< 5 cards) ──
  if (!isCarousel) {
    return (
      <div className={`mc-section ${className}`.trim()}>
        <div className="mc-grid">
          {allMetricIds.map((id) => (
            <div key={id} className="mc-grid__item">
              <div className="mc-removable-wrap">
                {renderCard(id)}

                {isEditMode && id.startsWith("custom-") && (
                  <button
                    type="button"
                    className="mc-remove-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveCustom?.(id);
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}

          {showAddButton && (
            <div
              className={`mc-grid__item ${
                inlineCard ? "mc-grid__item--inline" : "mc-grid__item--add"
              }`}
            >
              {renderAddOrInline()}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── CAROUSEL mode (≥ 5 cards) ──
  return (
    <div className={`mc-section mc-section--carousel ${className}`.trim()}>
      <div className="mc-carousel-row">
        {clampedIndex > 0 && (
          <button
            type="button"
            className="mc-nav-circle"
            onClick={() => navigate("prev")}
            aria-label="Précédent"
          >
            ‹
          </button>
        )}

        <div className="mc-carousel-viewport">
          <div
            ref={trackRef}
            className={`mc-carousel-track ${
              isAnimating ? `mc-carousel-track--${direction}` : ""
            }`}
            style={{ "--per-page": perPage }}
          >
            {visibleItems.map((id) => {
              if (id === ADD_ITEM_ID) {
                return (
                  <div
                    key={ADD_ITEM_ID}
                    className={`mc-carousel-slide ${
                      inlineCard
                        ? "mc-carousel-slide--inline"
                        : "mc-carousel-slide--add"
                    }`}
                  >
                    {renderAddOrInline()}
                  </div>
                );
              }

              return (
                <div key={id} className="mc-carousel-slide">
                  <div className="mc-removable-wrap">
                    {renderCard(id)}

                    {isEditMode && id.startsWith("custom-") && (
                      <button
                        type="button"
                        className="mc-remove-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onRemoveCustom?.(id);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {!isLastPage && (
          <button
            type="button"
            className="mc-nav-circle"
            onClick={() => navigate("next")}
            aria-label="Suivant"
          >
            ›
          </button>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mc-dots-row">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              type="button"
              key={i}
              className={`mc-dot ${i === clampedIndex ? "active" : ""}`}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}